import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { getLocalState } from "../../utils/localStorage";
import { DASHBOARD } from "../../utils/navigation";
import "../../App.css";
import { logoutRequest } from "../../store/auth/actions";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../../components/ui/navigation-menu";
import {
  Database,
  FileText,
  Gavel,
  Menu,
  User,
  ChevronDown,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";
import { TooltipProvider } from "../../components/ui/tooltip";
import ServicesDialog from "../../components/ServicesDialog";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = getLocalState("_currentUser");

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);

  // Fonction pour vérifier si le token a expiré
  const isTokenExpired = () => {
    const tokenExpiresAt = getLocalState("tokenExpiresAt");
    if (!tokenExpiresAt) return true;

    const expirationDate = new Date(tokenExpiresAt);
    const currentDate = new Date();

    return currentDate >= expirationDate;
  };

  // Fonction pour gérer le clic sur "Accès client"
  const handleAccessClient = (e) => {
    e.preventDefault();

    if (currentUser && isTokenExpired()) {
      setShowSessionExpiredModal(true);

      setTimeout(() => {
        dispatch(logoutRequest(navigate));
        setShowSessionExpiredModal(false);
      }, 3000);
    } else {
      navigate(DASHBOARD);
    }
  };

  const menuItems = [
    {
      label: "Entreprises",
      icon: (
        <Database className="mr-2 transition-transform data-[state=open]:scale-110" />
      ),
      link: "/",
      description:
        "Accédez à notre base de données complète des entreprises françaises",
    },
    {
      label: "Formalité",
      icon: (
        <FileText className="mr-2 transition-transform data-[state=open]:scale-110" />
      ),
      sublinks: [
        {
          label: "Dépôt d'annonces légales",
          link: "https://formalite.datagreffe.ci/annonce",
          description: "Publiez vos annonces légales en quelques clics",
        },
        {
          label: "Formalités entreprises",
          link: "https://formalite.datagreffe.ci/formalite/type-entreprise",
          description: "Simplifiez vos démarches administratives",
        },
      ],
    },
    {
      label: "Justice",
      icon: (
        <Gavel className="mr-2 transition-transform data-[state=open]:scale-110" />
      ),
      sublinks: [
        {
          label: "Décisions de justice",
          link: "https://justice.datagreffe.ci/",
          description: "Consultez notre base de données de jurisprudence",
        },
      ],
    },
    {
      label: "Texte de lois",
      icon: (
        <Gavel className="mr-2 transition-transform data-[state=open]:scale-110" />
      ),
      sublinks: [
        {
          label: "Texte de lois",
          link: "https://lois.datagreffe.ci",
          description: "Consultez notre base de données de textes législatifs et réglementaires",
        },
      ],
    },
    {
      label: "Annonces légales",
      icon: (
        <FileText className="mr-2 transition-transform data-[state=open]:scale-110" />
      ),
      sublinks: [
        {
          label: "Annonces légales",
          link: "https://annonces.datagreffe.ci/",
          description:
            "Publiez en ligne des annonces légales",
        },
      ],
    },
  ];

  const renderMenuItem = (item) => {
    if (item.disabled) {
      return (
        <NavigationMenuItem key={item.label}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center text-base font-medium cursor-not-allowed opacity-50 px-4 py-2">
                {React.cloneElement(item.icon, { className: "mr-2 transition-transform" })}
                <span className="text-gray-400">
                  {item.label}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Bientôt Disponible</p>
            </TooltipContent>
          </Tooltip>
        </NavigationMenuItem>
      );
    }

    return (
      <NavigationMenuItem key={item.label}>
        <NavigationMenuTrigger className="text-base font-medium">
          {item.icon}
          <span className="text-gray-700 hover:text-orange-500 transition-all duration-300">
            {item.label}
            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-orange-500 group-data-[state=open]:w-full hover:w-full transition-all duration-300" />
          </span>
        </NavigationMenuTrigger>
        {item.sublinks ? (
          <NavigationMenuContent>
            <ul className="grid w-[500px] gap-3 p-4 bg-white">
              {item.sublinks.map((sublink) => (
                <li key={sublink.label}>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={sublink.link}
                    className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-orange-50 hover:text-orange-500"
                  >
                    <div className="text-sm font-medium leading-none">
                      {sublink.label}
                    </div>
                    <p className="line-clamp-2 text-sm text-muted-foreground">
                      {sublink.description}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </NavigationMenuContent>
        ) : (
          <NavigationMenuContent>
            <ul className="grid w-[400px] gap-3 p-4">
              <li className="row-span-3">
                <Link
                  to={item.link}
                  className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-orange-50 to-orange-100 p-6 no-underline hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="mt-4 mb-2 text-lg font-medium text-orange-900">
                    {item.label}
                  </div>
                  <p className="text-sm leading-tight text-orange-700">
                    {item.description}
                  </p>
                </Link>
              </li>
            </ul>
          </NavigationMenuContent>
        )}
      </NavigationMenuItem>
    );
  };

  const renderMobileLink = (item) => {
    if (item.disabled) {
      return (
        <div
          key={item.label}
          className="flex items-center text-base font-medium py-2 px-3 opacity-50 cursor-not-allowed text-gray-400"
        >
          {item.icon} {item.label}
          <span className="ml-2 text-xs text-gray-500">(Bientôt Disponible)</span>
        </div>
      );
    }

    return item.sublinks ? (
      item.sublinks.map((sublink) => (
        <Link
          key={sublink.label}
          to={sublink.link}
          className="flex items-center text-base font-medium py-2 px-3 hover:bg-orange-50 hover:text-orange-500 rounded-md transition-colors"
        >
          {item.icon} {sublink.label}
        </Link>
      ))
    ) : (
      <Link
        key={item.label}
        to={item.link}
        className="flex items-center text-base font-medium py-2 px-3 hover:bg-orange-50 hover:text-orange-500 rounded-md transition-colors"
      >
        {item.icon} {item.label}
      </Link>
    );
  };

  // Icône WhatsApp SVG
  const WhatsAppIcon = () => (
    <svg
      className="h-4 w-4"
      fill="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );

  return (
    <>
      <header className="w-full border-b border-gray-100 bg-white shadow-soft sticky top-0 z-50">
        <div className="mx-lg">
          <div className="flex h-16 items-center justify-around">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img
                  className=" w-20"
                  src="/assets/images/logo-recoov.png"
                  loading="lazy"
                  alt="logo de Datagreffe"
                />
              </Link>

              <div className="hidden lg:flex items-center space-x-2">
                <TooltipProvider>
                  <NavigationMenu>
                    <NavigationMenuList>
                      {menuItems.map(renderMenuItem)}
                    </NavigationMenuList>
                  </NavigationMenu>
                </TooltipProvider>
              </div>
            </div>

            <div className="flex items-center">
              <div className="hidden md:flex items-center gap-4 mr-4">
                <a
                  href="https://wa.me/2250718426314"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-700 hover:text-orange-500 transition-colors group"
                >
                  <WhatsAppIcon />
                  <span className="text-sm font-medium ml-2">07 18 42 63 14</span>
                </a>
              </div>

              <div className="hidden md:flex items-center gap-2">
                <ServicesDialog
                  trigger={
                    <Button
                      variant="outline"
                      className="font-medium text-gray-700 hover:text-orange-500 hover:border-orange-500 hover:bg-orange-50 transition-all duration-300"
                    >
                      Nos Services
                    </Button>
                  }
                />
                <Button
                  variant="outline"
                  className="font-medium text-gray-700 hover:text-orange-500 hover:border-orange-500 hover:bg-orange-50 transition-all duration-300"
                >
                  API{" "}
                  <ChevronDown className="ml-1 h-4 w-4 transition-transform" />
                </Button>
                <button
                  onClick={handleAccessClient}
                  className="bg-orange-500 text-white text-center py-2 px-3 rounded-md font-medium flex items-center justify-center hover:bg-orange-600 transition-colors transform hover:-translate-y-1 hover:shadow-md duration-300"
                >
                  <User className="mr-2 h-5 w-5" />
                  {currentUser ? "Accès client" : "Connexion"}
                </button>
              </div>

              <Button
                variant="ghost"
                className="lg:hidden hover:bg-orange-100 hover:text-orange-500 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Menu className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="lg:hidden py-4 px-4 border-t border-gray-100 bg-white">
          <nav className="flex flex-col space-y-4">
            <a
              href="https://wa.me/2250718426314"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-base font-medium py-2 px-3 hover:bg-green-50 hover:text-green-500 rounded-md transition-colors"
            >
              <WhatsAppIcon />
              <span className="ml-2">07 18 42 63 14</span>
            </a>

            {menuItems.map(renderMobileLink)}

            <ServicesDialog
              trigger={
                <div className="flex items-center text-base font-medium py-2 px-3 hover:bg-orange-50 hover:text-orange-500 rounded-md transition-colors">
                  Nos Services
                </div>
              }
            />

            <Link
              to="/contact"
              className="flex items-center text-base font-medium py-2 px-3 hover:bg-orange-50 hover:text-orange-500 rounded-md transition-colors"
            >
              API
            </Link>

            <button
              onClick={handleAccessClient}
              className="bg-orange-500 text-white text-center py-2 px-3 rounded-md font-medium flex items-center justify-center hover:bg-orange-600 transition-colors transform hover:-translate-y-1 hover:shadow-md duration-300"
            >
              <User className="mr-2 h-5 w-5" />
              {currentUser ? "Accès client" : "Connexion"}
            </button>
          </nav>
        </div>
      )}

      {/* Modal de session expirée - Style similaire à l'image */}
      {showSessionExpiredModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 animate-in fade-in zoom-in duration-300">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Session expirée
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Votre session a expiré. Pour des raisons de sécurité, veuillez vous reconnecter pour continuer à utiliser votre compte.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowSessionExpiredModal(false)}
                  className="flex-1 bg-white text-gray-700 border border-gray-300 py-3 px-6 rounded-md font-medium hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    dispatch(logoutRequest(navigate));
                    setShowSessionExpiredModal(false);
                  }}
                  className="flex-1 bg-orange-500 text-white py-3 px-6 rounded-md font-medium hover:bg-orange-600 transition-colors"
                >
                  Se connecter à nouveau
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;